import React from 'react';
import { motion } from 'framer-motion';
import {
  Rocket,         // Startups
  Briefcase,      // HR agencies
  Sun,            // Solar energy
  GraduationCap,  // Educational institutes
  TrendingUp,     // Growing companies
} from 'lucide-react';

/**
 * TrustBar — single-sentence social-proof band shown right after the hero.
 *
 * Reads as one flowing line: "Trusted by startups, HR agencies, solar
 * energy businesses, educational institutes, and growing companies."
 * Each industry is highlighted as a chip with a matching icon.
 */

const INDUSTRIES = [
  { label: 'startups',              Icon: Rocket,        color: 'text-indigo-600  bg-indigo-50  ring-indigo-100'  },
  { label: 'HR agencies',           Icon: Briefcase,     color: 'text-violet-600  bg-violet-50  ring-violet-100'  },
  { label: 'solar energy businesses', Icon: Sun,         color: 'text-amber-600   bg-amber-50   ring-amber-100'   },
  { label: 'educational institutes', Icon: GraduationCap, color: 'text-emerald-600 bg-emerald-50 ring-emerald-100' },
  { label: 'growing companies',     Icon: TrendingUp,    color: 'text-rose-600    bg-rose-50    ring-rose-100'    },
];

export const TrustBar = () => {
  return (
    <section
      className="border-y border-slate-100 bg-white py-10 lg:py-14"
      data-testid="trust-bar"
      aria-label="Trusted by teams across industries"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-2 text-center text-sm sm:text-base leading-relaxed text-slate-600"
          data-testid="trust-bar-headline"
        >
          <span className="font-medium text-slate-700 mr-1">Trusted by</span>

          {INDUSTRIES.map((item, i) => {
            const { label, Icon, color } = item;
            return (
              <motion.span
                key={label}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.35, delay: 0.08 + i * 0.07 }}
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full ring-1 font-semibold text-xs sm:text-sm whitespace-nowrap ${color}`}
                data-testid={`trust-bar-chip-${i}`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2.4} />
                {label}
              </motion.span>
            );
          })}
        </motion.p>
      </div>
    </section>
  );
};

export default TrustBar;
